import { clone } from '@jkhy/frontend-utils/functions';
import { WireTransferProductFeatureModelDto } from '@treasury/api/bo';
import { CompanyProductFeature } from './company-product-feature.entity';

const defaultDto: WireTransferProductFeatureModelDto = {
    id: 0,
    type: 'WireTransfer',
    isConfigurable: false,
    productUniqueId: '',
    isInternational: false,
    dailyLimit: 0,
    isInternationalDisabled: false,
    requirePurpose: false,
    limitFutureDateEnabled: false,
    farthestWireExistInDays: 0,
    internationalUsdReadOnly: false,
    showInternationalUsd: false,
    paymentApprovalType: 'Overall',
};

type PaymentApprovalType = 'Overall' | 'Threshold';

export type PaymentThresholdApproval = {
    id: number;
    approvalCount: number;
    startAmount: number;
    endAmount: number;
};

export class WireProductFeature extends CompanyProductFeature<WireTransferProductFeatureModelDto> {
    constructor(dto: WireTransferProductFeatureModelDto = clone(defaultDto)) {
        super(dto);
    }

    public get dailyLimit(): number {
        return this.dto.dailyLimit ?? 0;
    }

    public set dailyLimit(dailyLimit: number) {
        this.dto.dailyLimit = dailyLimit;
    }

    public get allowInternational() {
        return this.dto.isInternational ?? false;
    }

    public set allowInternational(value: boolean) {
        this.dto.isInternational = value;
    }

    public get allowWireUpload() {
        return this.dto.allowWireFileUpload ?? false;
    }

    public set allowWireUpload(value: boolean) {
        this.dto.allowWireFileUpload = value;
    }

    public get limitFutureDatedWires() {
        return this.dto.limitFutureDateEnabled ?? false;
    }

    public set limitFutureDatedWires(value: boolean) {
        this.dto.limitFutureDateEnabled = value;
    }

    public get requirePurpose() {
        return this.dto.requirePurpose ?? false;
    }

    public set requirePurpose(value: boolean) {
        this.dto.requirePurpose = value;
    }

    public get maxNumberOfFutureDays() {
        return this.dto.maximumFutureDays ?? 5;
    }

    public set maxNumberOfFutureDays(value: number) {
        this.dto.maximumFutureDays = value;
    }

    public get creditorApprovers() {
        return Number(this.dto.beneficiaryApproval);
    }

    public set creditorApprovers(value: number | undefined) {
        this.dto.beneficiaryApproval = `${value}`;
    }

    public get templateApprovers() {
        return Number(this.dto.templateApproval);
    }

    public set templateApprovers(value: number | undefined) {
        this.dto.templateApproval = `${value}`;
    }

    public get paymentApprovalType() {
        return (this.dto.paymentApprovalType ?? 'Overall') as PaymentApprovalType;
    }

    public set paymentApprovalType(value: PaymentApprovalType) {
        this.dto.paymentApprovalType = value;
    }

    public get paymentApprovers() {
        return Number(this.dto.paymentApproval);
    }

    public set paymentApprovers(value: number | undefined) {
        this.dto.paymentApproval = `${value}`;
    }

    public get paymentThresholdApprovals() {
        return (this.dto.paymentThresholdApprovals ?? []).map(approval => ({
            id: approval.id,
            approvalCount: approval.approvalCount ?? 0,
            startAmount: approval.startAmount ?? 0,
            endAmount: approval.endAmount ?? Number.POSITIVE_INFINITY,
        }));
    }

    public set paymentThresholdApprovals(value: PaymentThresholdApproval[]) {
        this.dto.paymentThresholdApprovals = value.map(approval => ({
            id: approval.id,
            approvalCount: approval.approvalCount,
            startAmount: approval.startAmount,
            endAmount:
                approval.endAmount === Number.POSITIVE_INFINITY ? undefined : approval.endAmount,
        }));
    }
}
